import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, doc, addDoc, deleteDoc, updateDoc, query, where, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from './UserContext';

const ReadingListContext = createContext();

export function ReadingListProvider({ children }) {
  const { user } = useUser();
  const [myLists, setMyLists] = useState([]);

  useEffect(() => {
    if (!user) {
      setMyLists([]);
      return;
    }

    const q = query(collection(db, 'readingLists'), where('ownerId', '==', user.id));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => {
        const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
        return dateB - dateA;
      });
      setMyLists(data);
    });

    return () => unsubscribe();
  }, [user]);

  const createList = useCallback(async (name, description, emoji) => {
    if (!user) return;
    const docRef = await addDoc(collection(db, 'readingLists'), {
      name,
      description: description || '',
      emoji: emoji || '📚',
      ownerId: user.id,
      ownerName: user.name,
      books: [],
      isPublic: true,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  }, [user]);

  const deleteList = useCallback(async (listId) => {
    await deleteDoc(doc(db, 'readingLists', listId));
  }, []);

  const addBookToList = useCallback(async (listId, book) => {
    const list = myLists.find(l => l.id === listId);
    if (!list) return;
    if (list.books.find(b => b.id === book.id)) return;

    const bookData = {
      id: book.id,
      title: book.title,
      author: book.author,
      coverGradient: book.coverGradient,
      genre: book.genre,
    };

    await updateDoc(doc(db, 'readingLists', listId), {
      books: [...list.books, bookData],
    });
  }, [myLists]);

  const removeBookFromList = useCallback(async (listId, bookId) => {
    const list = myLists.find(l => l.id === listId);
    if (!list) return;
    await updateDoc(doc(db, 'readingLists', listId), {
      books: list.books.filter(b => b.id !== bookId),
    });
  }, [myLists]);

  return (
    <ReadingListContext.Provider value={{
      myLists, createList, deleteList, addBookToList, removeBookFromList,
    }}>
      {children}
    </ReadingListContext.Provider>
  );
}

export function useReadingLists() {
  return useContext(ReadingListContext);
}